import type {
  ChatMessage,
  SendMessageRequest,
  SendMessageResponse,
  OrchestratorConversation,
  OrchestratorMessage,
} from "../types/conversationTypes";
import { toChatMessage } from "./adapters";

const delay = (ms: number) => new Promise(r => setTimeout(r, ms));

const makeMsg = (p: Partial<OrchestratorMessage>): OrchestratorMessage => ({
  id: p.id ?? Math.random().toString(36).slice(2),
  timestamp: p.timestamp ?? new Date().toISOString(),
  sender: p.sender ?? "orchestrator",
  content: p.content ?? "",
  agentsInvolved: p.agentsInvolved,
  images: p.images,
});

const THREADS: Record<string, OrchestratorConversation> = {
  "mock-italy": {
    conversationId: "mock-italy",
    status: "active",
    messages: [
      makeMsg({
        sender: "orchestrator",
        content: "Ciao! Ai ales Italia — Roma, Firenze sau Amalfi?",
        images: [{ url: "https://images.unsplash.com/photo-1516483638261-f4dbaf036963" }],
      }),
      makeMsg({ sender: "user", content: "În septembrie." }),
    ],
    leadScore: 60,
  },
};

const createConv = (firstUserMessage?: string): OrchestratorConversation => {
  const id = "mock-" + Math.random().toString(36).slice(2, 8);
  const messages: OrchestratorMessage[] = [];

  if (firstUserMessage) {
    messages.push(makeMsg({ sender: "user", content: firstUserMessage }));
  }

  let content = "I can help you plan! Ce buget și perioadă preferi?";
  let images;
  if (firstUserMessage?.toLowerCase().includes("ital")) {
    content = "Italy e superbă. Când vrei să călătorești?";
    images = [
      { url: "https://images.unsplash.com/photo-1523906834658-6e24ef2386f9" },
    ];
  }
  messages.push(makeMsg({ sender: "orchestrator", content, images }));

  return { conversationId: id, status: "active", messages, leadScore: 50 };
};

export const mockApi = {
  async sendMessage(req: SendMessageRequest): Promise<SendMessageResponse> {
    await delay(400 + Math.random() * 600);

    // start new conv
    if (!req.conversationId) {
      const conv = createConv(req.message);
      THREADS[conv.conversationId] = conv;
      const ai = conv.messages.find(m => m.sender === "orchestrator")!;
      return { conversationId: conv.conversationId, messages: [toChatMessage(ai)] };
    }

    // existing conv
    const conv = THREADS[req.conversationId] ?? (THREADS[req.conversationId] = {
      conversationId: req.conversationId, status: "active", messages: [],
    });

    conv.messages.push(makeMsg({ sender: "user", content: req.message }));

    // content based response
    const txt = req.message.toLowerCase();
    let content = "Perfect. Spune-mi bugetul și datele aproximative.";
    let images;
    let options;
    if (txt.includes("beach") || txt.includes("plaj") || txt.includes("hawaii")) {
      content = "Hawaii e o idee bună. Uite 3 insule populare:";
      options = [
        { id: "maui", title: "Maui", description: "Plaje & resorturi", image: "https://images.unsplash.com/photo-1469041797191-50ace28483c3" },
        { id: "oahu", title: "Oahu", description: "Waikiki & Pearl Harbor", image: "https://images.unsplash.com/photo-1472396961693-142e6e269027" },
        { id: "kauai", title: "Kauai", description: "The Garden Isle", image: "https://images.unsplash.com/photo-1466721591366-2d5fba72006d" },
      ];
    } else if (txt.includes("ital")) {
      content = "Roma, Firenze, Amalfi — ce preferi?";
      images = [{ url: "https://images.unsplash.com/photo-1534445867742-43195f401b6c" }];
    }

    const ai = makeMsg({ sender: "orchestrator", content, images });
    conv.messages.push(ai);

    const chatAi: ChatMessage = {
      ...toChatMessage(ai),
      options: options,
    };

    return { conversationId: conv.conversationId, messages: [chatAi] };
  },

  async getConversations(): Promise<Array<{ id: string; title: string; date: string }>> {
    await delay(200);
    return Object.values(THREADS).map(c => ({
      id: c.conversationId,
      title: `Conversation ${c.conversationId}`,
      date: new Date(c.messages?.[0]?.timestamp ?? Date.now()).toISOString().slice(0,10),
    }));
  },

  async getConversation(conversationId: string): Promise<ChatMessage[]> {
    await delay(200);
    const conv = THREADS[conversationId];
    if (!conv) return [];
    return conv.messages.map(toChatMessage);
  },

  async updateConversation(conversationId: string, patch: { status?: string }) {
    await delay(150);
    const conv = THREADS[conversationId];
    if (conv && patch.status) conv.status = patch.status as any;
    return conv;
  },

  async deleteConversation(conversationId: string) {
    await delay(120);
    delete THREADS[conversationId];
    return true;
  },
  reset() {
    // No-op for mock; included for API shape consistency
  },
  switchConversation(_conversationId: string) {
    // No-op for mock
  },
};
